import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { get } from 'lodash';
import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { Hint } from '../Hint';

export type ControlledSimpleSelectProps<T extends FieldValues> = {
  fieldName: FieldPathByValue<T, string | undefined>;
  control: Control<T>;
  options: { value: string | undefined; label: string }[];
  hideErrorMessage?: boolean;
  label?: ReactNode;
  hint?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  fontSize?: 'u-fs-2' | 'u-fs-3';
  displayValue?: boolean;
};

export function ControlledSimpleSelect<T extends FieldValues>({
  control,
  fieldName,
  options = [],
  hideErrorMessage = false,
  label,
  hint,
  required,
  disabled,
  fontSize = 'u-fs-3',
  displayValue = false,
}: ControlledSimpleSelectProps<T>) {
  const {
    field,
    formState: { errors },
  } = useController({ control, name: fieldName });

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}

      {displayValue ? (
        <div className="u-fs-2">{typeof field.value === 'boolean' ? (field.value ? 'Yes' : 'No') : field.value}</div>
      ) : (
        <>
          <Form.Select
            {...field}
            className={classNames(fontSize, { 'u-border-color-red1': !!get(errors, fieldName) })}
            disabled={disabled}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value);
            }}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
          {hint && <Hint>{hint}</Hint>}
        </>
      )}

      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
